import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtUsd } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Term } from "@/components/Term";

export default function TradeModal({ open, onOpenChange, side, coin, onDone }) {
  const [cash, setCash] = useState(0);
  const [holding, setHolding] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount("");
    api.get("/portfolio").then((r) => {
      setCash(r.data.cash);
      const h = r.data.positions.find((p) => p.coin_id === coin.id);
      setHolding(h || null);
    });
  }, [open, coin?.id]);

  if (!coin) return null;

  const usdValue = Number(amount || 0);
  const qty = coin.price ? usdValue / coin.price : 0;

  const quick = (pct) => {
    const base = side === "buy" ? cash : (holding ? holding.market_value : 0);
    setAmount((base * pct / 100).toFixed(2));
  };

  const submit = async () => {
    if (!usdValue || usdValue <= 0) { toast.error("Enter an amount"); return; }
    if (side === "buy" && usdValue > cash) { toast.error("Insufficient virtual cash"); return; }
    if (side === "sell" && (!holding || usdValue > holding.market_value)) { toast.error("Not enough position"); return; }
    setBusy(true);
    try {
      await api.post("/portfolio/trade", {
        coin_id: coin.id,
        symbol: coin.symbol,
        name: coin.name,
        side,
        usd_amount: usdValue,
        price: coin.price,
      });
      toast.success(`${side === "buy" ? "Bought" : "Sold"} ${coin.symbol?.toUpperCase()} · ${fmtUsd(usdValue)}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trade failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trade-modal" className="bg-slate-950 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {side === "buy" ? "Buy" : "Sell"} {coin.name}
            <span className="text-slate-500 text-sm ml-2 font-mono-ticker uppercase">{coin.symbol}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between text-xs">
          <div className="text-slate-400"><Term k="pt_market_price" testid="tm-term-market-price">Market Price</Term></div>
          <div className="font-mono-ticker">{fmtUsd(coin.price)}</div>
        </div>
        <div className="flex items-center justify-between text-xs">
          <div className="text-slate-400"><Term k={side === "buy" ? "pt_virtual_cash" : "pt_position_value_sell"} testid="tm-term-balance">{side === "buy" ? "Virtual Cash" : "Position Value"}</Term></div>
          <div className="font-mono-ticker text-emerald-400">
            {fmtUsd(side === "buy" ? cash : (holding?.market_value || 0))}
          </div>
        </div>

        <div className="mt-4">
          <label className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500"><Term k="pt_amount_usd" iconClass="normal-case" testid="tm-term-amount">Amount (USD)</Term></label>
          <input
            data-testid="trade-amount-input"
            type="number" step="0.01" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full mt-1 apx-ring px-3 py-3 rounded-lg bg-slate-900 border border-slate-800 outline-none font-mono-ticker text-lg"
          />
          <div className="flex gap-2 mt-2">
            {[25, 50, 75, 100].map((p) => (
              <button
                key={p} onClick={() => quick(p)}
                disabled={side === "sell" && holding === null}
                data-testid={`quick-${p}`}
                className="flex-1 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-xs hover:bg-slate-800 font-mono-ticker disabled:opacity-40 disabled:cursor-wait"
              >
                {p}%
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-2 font-mono-ticker">
            <Term k="pt_est_qty" testid="tm-term-est-qty">≈ {qty.toFixed(6)} {coin.symbol?.toUpperCase()}</Term>
          </div>
        </div>

        <button
          data-testid="confirm-trade-btn"
          disabled={busy}
          onClick={submit}
          className={`mt-4 w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
            side === "buy"
              ? "bg-emerald-600 hover:bg-emerald-500 text-slate-950"
              : "bg-red-600 hover:bg-red-500 text-white"
          } disabled:opacity-60`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Confirm {side === "buy" ? "Buy" : "Sell"} (Paper)
        </button>
        <div className="text-[10px] text-amber-300/80 text-center mt-2">
          Simulated trade only · No real money
        </div>
      </DialogContent>
    </Dialog>
  );
}
